import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useRepository, Button } from '@city-sage/aurum-avis-labs-components';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { QueryKeys } from '../../../lib/QueryKeys';
import { Itinerary, ItineraryVisibilityEnum } from '@city-sage/core-client';
import { ItineraryPreviewStory } from './ItineraryPreviewStory';
import { motion } from 'framer-motion';
import { X, Globe, Compass, RotateCcw, MapPin } from 'lucide-react';

interface MapBounds {
  southWest: { lng: number; lat: number };
  northEast: { lng: number; lat: number };
}

interface ItineraryReelContainerProps {
  initialItineraryId?: string;
  bounds?: MapBounds;
  initialActiveIndex?: number;
}

export function ItineraryReelContainer({
  initialItineraryId,
  bounds,
  initialActiveIndex,
}: ItineraryReelContainerProps) {
  const { api } = useRepository();
  const navigate = useNavigate();
  const search = useSearch({ from: '/itineraries/reels/$startId' });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex ?? 0);
  const observerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hasScrolledToInitial = useRef(false);

  // Fetch the specific initial itinerary if provided
  const { data: initialItinerary } = useQuery({
    queryKey: ['itinerary-initial', initialItineraryId],
    queryFn: () => api.findOneItinerary({ id: initialItineraryId! }),
    enabled: !!initialItineraryId,
  });

  // Infinite query for loading more itineraries
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError } =
    useInfiniteQuery({
      queryKey: bounds
        ? QueryKeys.itinerary.byBoundsInfinite(bounds, { limit: 3 })
        : QueryKeys.itinerary.all({
            limit: 3,
            filter: JSON.stringify({
              visibility: ItineraryVisibilityEnum.Public,
            }),
          }),
      queryFn: async ({ pageParam = 1 }) => {
        let response: Itinerary[];

        if (bounds) {
          response = await api.getItinerariesByBounds({
            southWestLng: bounds.southWest.lng,
            southWestLat: bounds.southWest.lat,
            northEastLng: bounds.northEast.lng,
            northEastLat: bounds.northEast.lat,
            page: pageParam,
            limit: 3,
          });
        } else {
          response = await api.findAllItinerary({
            limit: 3,
            page: pageParam,
            filter: JSON.stringify({
              visibility: ItineraryVisibilityEnum.Public,
            }),
          });
        }

        return {
          results: response,
          nextPage: pageParam + 1,
          hasMore: response.length === 3,
        };
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        return lastPage.hasMore ? lastPage.nextPage : undefined;
      },
    });

  // Combine all itineraries
  const allItineraries = (() => {
    let infiniteResults = data?.pages.flatMap((page) => page.results) || [];
    infiniteResults = infiniteResults.filter(
      (it: Itinerary) => it.id !== initialItineraryId
    );

    if (initialItinerary) {
      return [initialItinerary, ...infiniteResults];
    }

    return infiniteResults;
  })();

  // Check if we should show the "explore all" end page
  const shouldShowExploreAllPage =
    bounds && !hasNextPage && allItineraries.length > 0;

  // Check if we should show the "start over" end page (reached end of all stories)
  const shouldShowStartOverPage =
    !bounds && !hasNextPage && allItineraries.length > 0;

  // Handler to explore all itineraries (remove bounds)
  const handleExploreAll = () => {
    // Navigate to the same reel but without bounds to load all itineraries
    if (allItineraries.length > 0) {
      navigate({
        to: '/itineraries/reels/$startId',
        params: { startId: allItineraries[0].id },
        search: { activeIndex: 0 },
      });
    }
  };

  // Handler to start over from the beginning
  const handleStartOver = () => {
    if (allItineraries.length > 0) {
      // Reset to the first itinerary and scroll to the top
      navigate({
        to: '/itineraries/reels/$startId',
        params: { startId: allItineraries[0].id },
        search: { activeIndex: 0 },
        replace: true,
      });
      
      // Reset scroll position and state
      hasScrolledToInitial.current = false;
      setActiveIndex(0);
      
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  };

  // Update URL when activeIndex changes (after initial load)
  useEffect(() => {
    if (hasScrolledToInitial.current && allItineraries.length > 0) {
      navigate({
        search: {
          ...search,
          activeIndex: activeIndex,
        },
        replace: true,
      });
    }
  }, [activeIndex, navigate, search, allItineraries.length]);

  // Intersection Observer to track active story and load more
  useEffect(() => {
    if (!scrollContainerRef.current || allItineraries.length === 0) return;

    const observers: IntersectionObserver[] = [];
    let isInitialized = false;

    observerRefs.current.forEach((ref, index) => {
      if (!ref) {
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveIndex(index);

            // Load more when approaching the end
            if (
              index >= allItineraries.length - 3 &&
              hasNextPage &&
              !isFetchingNextPage
            ) {
              fetchNextPage();
            }
          }
        },
        {
          root: scrollContainerRef.current,
          threshold: 0.5, // Lower threshold to test
          rootMargin: '0px', // Remove margins to test
        }
      );

      observer.observe(ref);
      observers.push(observer);
    });

    // Set initial active index only once
    if (!isInitialized && allItineraries.length > 0) {
      setActiveIndex(0);
      isInitialized = true;
    }

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [allItineraries.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Prevent body scroll when reel is open
  useEffect(() => {
    if (initialItineraryId) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
      };
    }
  }, [initialItineraryId]);

  // Set initial scroll position when content loads (only once)
  useEffect(() => {
    if (
      scrollContainerRef.current &&
      allItineraries.length > 0 &&
      !hasScrolledToInitial.current
    ) {
      let targetIndex = 0;

      // Prioritize activeIndex from URL (for returning users)
      if (
        initialActiveIndex !== undefined &&
        initialActiveIndex < allItineraries.length
      ) {
        targetIndex = initialActiveIndex;
      }
      // Fallback to finding the initial itinerary (for first-time users)
      else if (initialItinerary) {
        const foundIndex = allItineraries.findIndex(
          (it) => it.id === initialItineraryId
        );
        if (foundIndex >= 0) {
          targetIndex = foundIndex;
        }
      }

      hasScrolledToInitial.current = true;
      setActiveIndex(targetIndex);

      // Set scroll position immediately without animation
      if (scrollContainerRef.current) {
        // Temporarily disable smooth scrolling for instant positioning
        const container = scrollContainerRef.current;
        container.style.scrollBehavior = 'auto';
        container.scrollTop = targetIndex * window.innerHeight;

        // Re-enable smooth scrolling after a brief moment
        setTimeout(() => {
          container.style.scrollBehavior = 'smooth';
        }, 100);
      }
    }
  }, [
    initialItinerary,
    allItineraries.length,
    initialItineraryId,
    initialActiveIndex,
  ]);

  if (!initialItineraryId || allItineraries.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background"
    >
      {/* Close Button */}
      <div className="absolute top-10 right-4 z-50">
        <Link to="/">
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-white hover:bg-white/20 rounded-full backdrop-blur-md bg-black/30"
          >
            <X className="h-5 w-5" />
          </Button>
        </Link>
      </div>

      {/* Scrollable Container with CSS Scroll Snap */}
      <div
        ref={scrollContainerRef}
        className="h-full w-full overflow-y-auto [&::-webkit-scrollbar]:hidden"
        style={{
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          // Enhanced touch scrolling for iOS/mobile
          overscrollBehavior: 'none',
          // Smooth momentum scrolling
          scrollPaddingTop: '0px',
          // Hide scrollbar
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
        }}
      >
        {allItineraries.map((itinerary, index) => {
          const isActive = index === activeIndex;

          return (
            <div
              key={itinerary.id}
              ref={(el) => {
                observerRefs.current[index] = el;
              }}
              className="h-dvh w-full flex-shrink-0"
              style={{
                scrollSnapAlign: 'start',
                scrollSnapStop: 'always',
              }}
            >
              <ItineraryPreviewStory
                itinerary={itinerary}
                isActive={isActive}
              />
            </div>
          );
        })}

        {/* Explore All Page - shown when bounds are present and no more results */}
        {shouldShowExploreAllPage && (
          <div
            key="explore-all-page"
            ref={(el) => {
              observerRefs.current[allItineraries.length] = el;
            }}
            className="h-dvh w-full flex-shrink-0 bg-white relative"
            style={{
              scrollSnapAlign: 'start',
              scrollSnapStop: 'always',
            }}
          >
            {/* Content */}
            <div className="h-full flex flex-col items-center justify-center p-8 text-center text-foreground">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                {/* Icon */}
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <Globe className="h-8 w-8 text-primary" />
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-foreground">
                    No more stories in this area
                  </h2>
                  <p className="text-muted-foreground text-lg">
                    You've reached the end of itineraries for this location
                  </p>
                </div>

                {/* Description */}
                <p className="text-muted-foreground max-w-sm text-base">
                  Ready to discover more amazing destinations? Explore all
                  travel stories from around the world.
                </p>

                {/* Action Button */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <Button
                    onClick={handleExploreAll}
                    size="lg"
                    className="font-semibold px-8 py-3 text-base"
                  >
                    <Compass className="h-5 w-5 mr-2" />
                    Explore All Stories
                  </Button>
                </motion.div>

                {/* Subtitle */}
                <p className="text-muted-foreground text-sm">
                  Discover travel inspiration worldwide
                </p>
              </motion.div>
            </div>
          </div>
        )}

        {/* Start Over Page - shown when no bounds and no more results */}
        {shouldShowStartOverPage && (
          <div
            key="start-over-page"
            ref={(el) => {
              observerRefs.current[allItineraries.length] = el;
            }}
            className="h-dvh w-full flex-shrink-0 bg-white relative"
            style={{
              scrollSnapAlign: 'start',
              scrollSnapStop: 'always',
            }}
          >
            {/* Content */}
            <div className="h-full flex flex-col items-center justify-center p-8 text-center text-foreground">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                {/* Title */}
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-foreground">
                    You've seen it all!
                  </h2>
                  <p className="text-muted-foreground text-lg">
                    You've explored every travel story available
                  </p>
                </div>

                {/* Description */}
                <p className="text-muted-foreground max-w-sm text-base">
                  What would you like to do next? Start over from the beginning or explore stories from a specific location.
                </p>

                {/* Action Buttons */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                  className="space-y-3 w-full"
                >
                  <Button
                    onClick={handleStartOver}
                    size="lg"
                    className="w-full font-semibold px-8 py-3 text-base"
                  >
                    <RotateCcw className="h-5 w-5 mr-2" />
                    Start Over
                  </Button>
                  
                  <Link to="/" className="block">
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full font-semibold px-8 py-3 text-base"
                    >
                      <MapPin className="h-5 w-5 mr-2" />
                      Explore by Location
                    </Button>
                  </Link>
                </motion.div>

                {/* Subtitle */}
                <p className="text-muted-foreground text-sm">
                  Keep exploring amazing travel adventures
                </p>
              </motion.div>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {isFetchingNextPage && (
          <div className="h-20 flex items-center justify-center text-white bg-black/50">
            <div className="px-4 py-2 rounded-full bg-black/70 text-white text-sm">
              Loading more stories...
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
